import { AxiosError } from 'axios';
import React, { useState } from 'react';

import { AlertMessage } from '@amzn/imdb-shared-meridian-components/components/InputForm/InputForm';
import { SelectInput } from '@amzn/imdb-shared-meridian-components/components/InputForm/SelectInput/SelectInput';
import { TextareaInput } from '@amzn/imdb-shared-meridian-components/components/InputForm/TextareaInput/TextareaInput';
import { InputModal } from '@amzn/imdb-shared-meridian-components/components/InputModal';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import FileInput, { FileDetails } from '@amzn/meridian/file-input';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import { ModalProps } from '@amzn/meridian/modal/modal';
import RadioButton from '@amzn/meridian/radio-button';
import { SelectOptionProps } from '@amzn/meridian/select/select-option';
import Text from '@amzn/meridian/text';

import { WorkflowType } from '../../../listoramaAdmin-api/generated-src';
import { InputType } from './types';

const DATA_DELETION_INPUT_TYPES = [
    { value: InputType.CUSTOMER_ID, label: 'Customer ID' },
    { value: InputType.LIST_ID, label: 'List ID' }
];

interface BulkWorkflowFormViewInput {
    inputFile?: File;
    workflowType?: WorkflowType;
    workflowContext?: string;
    inputType?: InputType;
    dryRun?: boolean;
}

interface BulkWorkflowResponseData {
    skippedInputLines?: string[];
    fetching?: boolean;
    error?: AxiosError;
}

interface BulkWorkflowFormViewProps {
    responseData: BulkWorkflowResponseData;
    onInputChange: () => void;
    isModalOpen: boolean;
    setIsModalOpen: (_: boolean) => void;
    validateInput: (_: BulkWorkflowFormViewInput) => void;
    uploadInput: (_: BulkWorkflowFormViewInput) => void;
}

export const BulkWorkflowFormView: React.FC<BulkWorkflowFormViewProps> = (props) => {
    const { onInputChange, responseData, isModalOpen, setIsModalOpen, uploadInput, validateInput } = props;

    const [inputData, setInputData] = useState<BulkWorkflowFormViewInput>({});
    const [inputStyle, setInputStyle] = useState('manual');
    const [percentage, setPercentage] = useState<number>(0);
    const [inputLines, setInputLines] = useState('');

    // Allow for workflows to be enabled only in backend
    const getWorkflowSelectOptions = (): SelectOptionProps[] => {
        return [
            {
                value: WorkflowType.DataDeletion,
                label: WorkflowType.DataDeletion
            },
            {
                value: WorkflowType.UpdateAlternateOrdering,
                label: WorkflowType.UpdateAlternateOrdering
            },
            {
                value: WorkflowType.SyncFollowLists,
                label: WorkflowType.SyncFollowLists
            },
            {
                value: WorkflowType.ListSpamDetection,
                label: WorkflowType.ListSpamDetection
            },
            {
                value: WorkflowType.ItemDescriptionSpamDetection,
                label: WorkflowType.ItemDescriptionSpamDetection
            },
            {
                value: WorkflowType.SeedListItems,
                label: WorkflowType.SeedListItems
            }
        ];
    };

    const setWorkflowType = (workflowType: WorkflowType) => {
        setInputData({ ...inputData, ...{ workflowType: workflowType } });
        onInputChange();
    };

    const setWorkflowContext = (workflowContext: string) => {
        setInputData({ ...inputData, ...{ workflowContext: workflowContext } });
        onInputChange();
    };

    const setInputType = (inputType: InputType) => {
        setInputData({ ...inputData, ...{ inputType: inputType } });
        onInputChange();
    };

    const showLoader = () => {
        return (
            <Column width='50%' alignmentHorizontal='center'>
                <Loader />
                <Text>Processing Bulk Workflow...</Text>
            </Column>
        );
    };

    const onClickCancel = () => {
        setInputData({ ...inputData, ...{ inputFile: undefined } });
        onInputChange();
        setPercentage(0);
    };

    const onClickRemoveFile = () => {
        setInputData({ ...inputData, ...{ inputFile: undefined } });
        onInputChange();
        setPercentage(0);
    };

    const showInputLines = (): string => inputLines;

    const onFileInput = (acceptedFiles: File[]) => {
        const newFile = acceptedFiles[0];

        const newInputData = { ...inputData, ...{ inputFile: newFile } };
        setInputData(newInputData);
        onInputChange();
        setPercentage(100);
        return newInputData;
    };

    /**
     * Bundles the manual input into a file with appropriate csv headers and validates the file.
     */
    const onManualInputSubmit = () => {
        if (!inputData.workflowType) {
            throw new Error(`WorkflowType not selected before input allowed.`);
        }

        const blob = new Blob([generateFileHeaders(inputData.workflowType, inputData.inputType) + '\n' + inputLines], {
            type: 'text/plain'
        });
        const file = new File([blob], 'lorAdminTestFile.txt', { type: 'text/plain' });

        validateInput(onFileInput([file]));
    };

    const onFileInputSubmit = () => {
        validateInput(inputData);
    };

    const onModalSubmit = () => {
        uploadInput(inputData);
    };

    const showBulkWorkflowForm = () => {
        const alertMessage = getAlertMessage(responseData);

        return (
            <Column width='50%'>
                <InputModal
                    setIsOpen={setIsModalOpen}
                    modalProps={createModalProps(isModalOpen, inputData?.workflowType)}
                    onSubmit={onModalSubmit}
                />
                {alertMessage && (
                    <Alert type={alertMessage.type}>
                        {alertMessage.message.split('\n').map((m) => (
                            <div key={m}>{m}</div>
                        ))}
                    </Alert>
                )}
                <Text tag='label' type='b300' color='primary'>
                    Workflow
                </Text>
                <SelectInput
                    id={'workflowTypeDropdown'}
                    options={getWorkflowSelectOptions()}
                    value={inputData?.workflowType || ''}
                    onChange={setWorkflowType}
                    placeholder='Enter Worklow...'
                />
                {inputData?.workflowType === WorkflowType.DataDeletion && (
                    <>
                        <Text tag='label' type='b300' color='primary'>
                            Data Type
                        </Text>
                        {DATA_DELETION_INPUT_TYPES.map((inputType) => (
                            <RadioButton
                                key={inputType.value}
                                checked={inputData?.inputType === inputType.value}
                                onChange={() => setInputType(inputType.value)}
                                name='inputType'
                                value={inputType.value}
                                size='small'
                            >
                                {inputType.label}
                            </RadioButton>
                        ))}
                    </>
                )}
                {(inputData?.workflowType === WorkflowType.ListSpamDetection ||
                    inputData?.workflowType === WorkflowType.ItemDescriptionSpamDetection) && (
                    <Checkbox
                        checked={inputData?.dryRun || false}
                        onChange={(checked) => {
                            setInputData({ ...inputData, dryRun: checked });
                            onInputChange();
                        }}
                    >
                        Dry run (detect and log spam without flagging lists)
                    </Checkbox>
                )}
                <Text tag='label' type='b300' color='primary'>
                    Workflow Context
                </Text>
                <Text tag='label' type='b200' color='primary'>
                    Provide a SIM/tt link, or a simple explanation as to why the work is being done. Avoid including any
                    personal-identifying customer information (such as emails).
                </Text>
                <Input
                    id={'context'}
                    type='text'
                    value={inputData?.workflowContext || ''}
                    onChange={setWorkflowContext}
                    placeholder='Enter context...'
                />
                <Text tag='label' type='b300' color='primary'>
                    Input Method
                </Text>
                <RadioButton
                    checked={inputStyle === 'manual'}
                    onChange={setInputStyle}
                    name='inputStyle'
                    value='manual'
                    size='small'
                >
                    Manual Input
                </RadioButton>
                <RadioButton
                    checked={inputStyle === 'fileBased'}
                    onChange={setInputStyle}
                    name='inputStyle'
                    value='fileBased'
                    size='small'
                >
                    File Input
                </RadioButton>
                {inputStyle === 'manual' && (
                    <>
                        <TextareaInput
                            id='bulkWorkflowInput'
                            value={showInputLines()}
                            onChange={setInputLines}
                            rows={10}
                            helperText={getHelpMessage(inputData?.workflowType, inputData?.inputType)}
                        />
                        <Button onClick={onManualInputSubmit}>Submit</Button>
                    </>
                )}

                {inputStyle === 'fileBased' && (
                    <FileInput type='single' onFileAttached={onFileInput} uploadButtonLabel={'Attach File'}>
                        {inputData.inputFile && (
                            <FileDetails
                                file={inputData.inputFile}
                                onClickCancel={onClickCancel}
                                onClickRemoveFile={onClickRemoveFile}
                                connectionSpeed='5MB/s'
                                cancelUploadButtonLabel={`Cancel, ${inputData.inputFile.name}`}
                                removeFileButtonLabel={`Delete, ${inputData.inputFile.name}`}
                                uploadPercentage={percentage}
                                uploadComplete={percentage === 100}
                            />
                        )}
                    </FileInput>
                )}
                {inputStyle === 'fileBased' && inputData.inputFile && (
                    <Button onClick={onFileInputSubmit}>Submit</Button>
                )}
            </Column>
        );
    };

    if (responseData.fetching) {
        return showLoader();
    } else {
        return showBulkWorkflowForm();
    }
};

const createModalProps = (isOpen: boolean, workflow?: WorkflowType): ModalProps => {
    return {
        title: 'Bulk Workflow',
        open: isOpen,
        children: [
            <Text key='description'>{`Are you sure you want to trigger a bulk workflow for these accounts${
                workflow ? ` for the ${workflow} Workflow` : ''
            }?`}</Text>
        ]
    };
};

const getAlertMessage = (responseData: BulkWorkflowResponseData): AlertMessage | undefined => {
    if (responseData.error) {
        return {
            type: 'error',
            message: responseData.error.message
        };
    }
    return undefined;
};

export const generateFileHeaders = (workflowType: WorkflowType, inputType?: InputType): string => {
    switch (workflowType) {
        case WorkflowType.DataDeletion:
            if (!inputType) {
                throw new Error('Input type is required for data deletion workflow');
            }
            return inputType;
        case WorkflowType.UpdateAlternateOrdering:
            return InputType.LIST_ID;
        case WorkflowType.SyncFollowLists:
            return InputType.USER_ID;
        case WorkflowType.ListSpamDetection:
            return InputType.LIST_ID;
        case WorkflowType.ItemDescriptionSpamDetection:
            return InputType.LIST_ID;
        case WorkflowType.SeedListItems:
            return 'userId,listClass,size,allowDuplicates,listId';
        default:
            throw new Error(
                `Unsupported WorkflowType selected: ${workflowType}, please update file headers for this case`
            );
    }
};

export const getHelpMessage = (workflowType?: WorkflowType, inputType?: InputType): string => {
    if (!workflowType) {
        return '';
    }

    switch (workflowType) {
        case WorkflowType.DataDeletion:
            if (inputType === InputType.LIST_ID) {
                return 'Enter listId values one per line.';
            } else if (inputType === InputType.CUSTOMER_ID) {
                return 'Enter customerId values one per line.';
            }
            return 'Please select a data type first.';
        case WorkflowType.UpdateAlternateOrdering:
            return 'Enter listId values one per line to update alternate ordering.';
        case WorkflowType.SyncFollowLists:
            return 'Enter userId values one per line to sync Follow lists from ListService to ListORama.';
        case WorkflowType.ListSpamDetection:
            return 'Enter listId values one per line to run spam detection.';
        case WorkflowType.ItemDescriptionSpamDetection:
            return 'Enter listId values one per line to run item description spam detection.';
        case WorkflowType.SeedListItems:
            return (
                'Enter one list per line as: userId,listClass,size[,allowDuplicates][,listId]. ' +
                'listClass is LIST, WATCHLIST, or CHECKINS. size is the target total item count ' +
                '(capped at 30000). For an existing regular LIST, provide its listId to top it up; ' +
                'omit listId to create a new list. WATCHLIST/CHECKINS are get-or-created per user.'
            );
        default:
            throw new Error(
                `Unsupported WorkflowType selected: ${workflowType}, please update help message for this case`
            );
    }
};
